import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiHardDrive, FiCpu, FiAlertCircle } from 'react-icons/fi';

const STATUS_DOT = {
  ON:    'cs-dot cs-dot-on',
  OFF:   'cs-dot cs-dot-off',
  ERROR: 'cs-dot cs-dot-idle',
};
const STATUS_COLOR = { ON: '#10b981', OFF: '#ef4444', ERROR: '#f59e0b' };

const parseGB = (text) => {
  if (!text) return null;
  const m = text.match(/([\d.,]+)/);
  if (!m) return null;
  let s = m[1];
  // Formato brasileiro: vírgula = decimal, ponto = milhar (ex: "1.024,50")
  // Formato US: ponto = decimal (ex: "1024.50")
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  }
  const val = parseFloat(s);
  return isNaN(val) ? null : val;
};

const freeColor = (gb) => {
  if (gb === null) return '#6b7280';
  if (gb < 1.0)   return '#ef4444';
  if (gb < 2.0)   return '#f59e0b';
  return '#10b981';
};

const stripLabel = (text, prefix) => text?.replace(prefix, '').trim() || '';

const Bar = ({ livreGB, totalGB, icon }) => {
  if (!totalGB || livreGB === null) return null;
  const usedPct = Math.min(100, Math.round((1 - livreGB / totalGB) * 100));
  const color   = freeColor(livreGB);
  const livreLabel = livreGB % 1 === 0 ? `${livreGB} GB` : `${livreGB.toFixed(1)} GB`;
  return (
    <div className="cs-srv-bar-row">
      {icon}
      <div className="cs-srv-track">
        <div className="cs-srv-fill" style={{ width: `${usedPct}%`, background: color }} />
      </div>
      <span className="cs-srv-bar-label" style={{ color }}>{livreLabel} livre</span>
    </div>
  );
};

const ServidoresAWS = () => {
  const [serverData, setServerData]   = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState(null);
  const isFetchingRef                 = useRef(false);

  const fetchServerData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 30000);
      const res = await fetch('https://v1-csapi.compsoft.com.br:823/aws', {
        headers: { Authorization: 'Basic ' + btoa('Portal:Portal1163') },
        signal: controller.signal,
      });
      clearTimeout(tid);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setServerData(json);
    } catch (err) {
      setError(err.name === 'AbortError' ? 'Timeout na conexão' : 'Erro ao carregar');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchServerData();
    const t = setInterval(fetchServerData, 3 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchServerData]);

  if (error) {
    return (
      <div className="cs-srv-error">
        <FiAlertCircle size={13} /> {error}
      </div>
    );
  }

  if (isLoading && serverData.length === 0) {
    return (
      <div className="cs-srv-skeleton">
        {[1, 2, 3].map(i => <div key={i} className="cs-skeleton-row" />)}
      </div>
    );
  }

  if (!serverData.length) {
    return <div className="cs-srv-empty">Nenhum servidor encontrado</div>;
  }

  return (
    <div className="cs-servers">
      {serverData.map((server, i) => {
        const diskLivre = parseGB(stripLabel(server.Livre,        'Espaço Livre:'));
        const diskTotal = parseGB(stripLabel(server.Total,        'Espaço Total:'));
        const memLivre  = parseGB(stripLabel(server.MemoriaLivre, 'Memória Livre:'));
        const memTotal  = parseGB(stripLabel(server.MemoriaTotal, 'Memória Total:'));

        return (
          <div key={server.servidor || i} className="cs-server-row">
            <div className="cs-server-header">
              <span className={STATUS_DOT[server.status] || 'cs-dot cs-dot-idle'} />
              <span className="cs-server-name">{server.servidor}</span>
              <span className="cs-server-badge" style={{ color: STATUS_COLOR[server.status] || '#6b7280' }}>
                {server.status}
              </span>
            </div>
            <Bar livreGB={diskLivre} totalGB={diskTotal} icon={<FiHardDrive size={10} color="#9ca3af" />} />
            <Bar livreGB={memLivre}  totalGB={memTotal}  icon={<FiCpu       size={10} color="#9ca3af" />} />
          </div>
        );
      })}
    </div>
  );
};

export default ServidoresAWS;
